import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@vds/icons";
import { Button } from "@vds/buttons";
import { Modal, ModalTitle, ModalBody, ModalFooter } from "@vds/modals";
import { Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { APPEARANCE_TEXT, DEPLOY_LOCKED_TOOLTIP, APPEARANCE_RUNTIME_STATUS } from "../../../constants/enrolledAppearanceConstants";
import { Loader } from "@vds/loaders";
import { enrollAppearanceRequest, resetEnrollStates } from "../../../store/actions/ea-actions";
import { Notification } from "@vds/notifications";
import { Title } from "@vds/typography";
import { Checkbox } from "@vds/checkboxes";
import { Line } from "@vds/lines";
import {
  CheckboxContainer,
  DeployModalContainer,
  DeployModalContent,
  DeploySelectedCountSpan,
  ImageStatusBadge,
  LoaderWrapper,
  ModalCloseButton,
  ModalTitleInfoIcon,
  ModalTitleRow,
  NotificationContainer,
  SiteIconPlaceholder,
  SitesAccordionContainer,
  SitesAccordionScrollContainer,
  ViewOnlySiteRow
} from "../EAList/styles/ea-styles";

const DeployModal = ({
  appearanceName,
  appearanceId,
  tagids,
  appearanceStatus,
  deployments = [],
  opened,
  closeModal,
  onDeploySuccess,
  onDeployBlockAttempt,
  onDeployFailure,
  viewOnly = false,
  enrollOnly = false,
  infoMessage,
  requiredSiteId
}) => {
  const [selectedSiteIds, setSelectedSiteIds] = useState(
    new Set([...(tagids || []), ...(requiredSiteId ? [requiredSiteId] : [])])
  );
  const enrollTriggered = useRef(false);
  const deployedSiteIds = new Set(tagids || []);

  const dispatch = useDispatch();

  const siteList = useSelector((state) => state.sitesReducer.siteTagList.sites) || [];
  const { enrolling, enrollError, enrollSuccess } = useSelector((state) => state.enrolledAppearanceReducer);

  const handleEsc = useCallback((e) => {
    if (e.key === "Escape") {
      dispatch(resetEnrollStates());
      closeModal();
    }
  }, [dispatch, closeModal]);

  useEffect(() => {
    if (!opened) return;
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [opened, handleEsc]);

  useEffect(() => {
    if (enrollSuccess && enrollTriggered.current) {
      enrollTriggered.current = false;
      dispatch(resetEnrollStates());
      closeModal();
      onDeploySuccess?.();
    }
  }, [enrollSuccess]);

  useEffect(() => {
    if (enrollError && enrollTriggered.current && onDeployFailure) {
      enrollTriggered.current = false;
      dispatch(resetEnrollStates());
      closeModal();
      onDeployFailure(enrollError);
    }
  }, [enrollError]);

  useEffect(() => {
    if (!enrollError || onDeployFailure) return;
    const timer = setTimeout(() => dispatch(resetEnrollStates()), 5000);
    return () => clearTimeout(timer);
  }, [enrollError]);

  const toggleSite = (siteId) => {
    if (deployedSiteIds.has(siteId) && selectedSiteIds.has(siteId) && deployments.length >= 1) {
      onDeployBlockAttempt?.();
      return;
    }
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const sitesToAdd = [...selectedSiteIds].filter((id) => !deployedSiteIds.has(id));
  const sitesToRemove = [...deployedSiteIds].filter((id) => !selectedSiteIds.has(id));
  const hasSiteDelta = sitesToAdd.length > 0 || sitesToRemove.length > 0;

  const handleEnroll = () => {
    enrollTriggered.current = true;
    dispatch(enrollAppearanceRequest({ appearanceId, sitesToAdd, sitesToRemove, appearanceStatus, enrollOnly }));
  };

  return (
    <Modal
      id="appearance-deploy-modal"
      opened={opened}
      surface="light"
      fullScreenDialog={false}
      disableAnimation={false}
      disableOutsideClick={true}
      ariaLabel="Deploy Modal"
      hideCloseButton={true}
      closeButton={
        <ModalCloseButton
          onClick={() => {
            dispatch(resetEnrollStates());
            closeModal();
          }}
        >
          <Icon name="close" />
        </ModalCloseButton>
      }
    >
      <ModalTitle>
        <ModalTitleRow>
          {viewOnly ? `${APPEARANCE_TEXT.VIEW_ENROLLED_SITES_TITLE} ` : `${APPEARANCE_TEXT.DEPLOY_MODAL_TITLE} `}
          <i>
            <b>{appearanceName}</b>
          </i>
          {!viewOnly && (
            <Tooltip title={DEPLOY_LOCKED_TOOLTIP} placement="bottom" arrow>
              <ModalTitleInfoIcon>
                <InfoOutlined fontSize="small" />
              </ModalTitleInfoIcon>
            </Tooltip>
          )}
        </ModalTitleRow>
      </ModalTitle>
      <ModalBody>
        {infoMessage && <p>{infoMessage}</p>}
        {!viewOnly && enrolling && (
          <LoaderWrapper>
            <Loader active={enrolling} fullscreen={false} />
          </LoaderWrapper>
        )}
        {!viewOnly && enrollError && !onDeployFailure && (
          <NotificationContainer>
            <Notification
              type="error"
              title={APPEARANCE_TEXT.DEPLOY_TO_COMPANION_ERROR}
              subtitle={enrollError || APPEARANCE_TEXT.PLEASE_TRY_AGAIN}
              disableFocus={false}
            />
          </NotificationContainer>
        )}
        <DeployModalContent>
          {!requiredSiteId && <SitesAccordionContainer>
            <Title size="small" viewport="desktop" bold={true}>
              {APPEARANCE_TEXT.SITES}
            </Title>
            {!viewOnly && !requiredSiteId && (
              <>
                <CheckboxContainer>
                  <Checkbox
                    id="select-all-sites"
                    selected={siteList.length > 0 && selectedSiteIds.size === siteList.length}
                    onChange={() => {
                      if (siteList.length > 0 && selectedSiteIds.size === siteList.length) {
                        setSelectedSiteIds(new Set());
                      } else {
                        setSelectedSiteIds(new Set(siteList.map((site) => site.id)));
                      }
                    }}
                    children={
                      <span className="labelStyle">
                        {APPEARANCE_TEXT.SELECT_ALL}
                        {selectedSiteIds.size > 0 && <DeploySelectedCountSpan>{selectedSiteIds.size} selected</DeploySelectedCountSpan>}
                      </span>
                    }
                  />
                </CheckboxContainer>
                <Line id="hoizontal-line" type="secondary" />
              </>
            )}
            <SitesAccordionScrollContainer>
              {requiredSiteId ? (
                <ViewOnlySiteRow>
                  <SiteIconPlaceholder hidden />
                  <span className="labelStyle">{siteList.find((site) => site.id === requiredSiteId)?.name}</span>
                </ViewOnlySiteRow>
              ) : (siteList || []).map((site) => {
                const isEnrolled = deployedSiteIds.has(site.id);
                if (viewOnly) {
                  return (
                    <ViewOnlySiteRow key={site.id}>
                      {isEnrolled ? <Icon name="checkmark" size="small" color="#026f28" /> : <SiteIconPlaceholder />}
                      <span className="labelStyle">{site.name}</span>
                      {isEnrolled && <ImageStatusBadge className="deploy-modal-badge">{APPEARANCE_TEXT.ENROLLED_LABEL}</ImageStatusBadge>}
                    </ViewOnlySiteRow>
                  );
                }
                return (
                  <CheckboxContainer key={site.id}>
                    <Checkbox
                      id={`site-${site.id}`}
                      selected={selectedSiteIds.has(site.id)}
                      onChange={() => toggleSite(site.id)}
                      children={<span className="labelStyle">{site.name}</span>}
                    />
                  </CheckboxContainer>
                );
              })}
            </SitesAccordionScrollContainer>
          </SitesAccordionContainer>}
        </DeployModalContent>
      </ModalBody>
      {!viewOnly && (
        <ModalFooter>
          <DeployModalContainer>
            <Button
              size="large"
              use="primary"
              onClick={handleEnroll}
              disabled={enrolling || (!hasSiteDelta && appearanceStatus !== APPEARANCE_RUNTIME_STATUS.VALID)}
            >
              {APPEARANCE_TEXT.DEPLOY_TO_COMPANION}
            </Button>
          </DeployModalContainer>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default DeployModal;
